/**
 * Custom Hashing Engine & Cryptographic Integrity Adapter (Package: @sovereign/auth)
 * Aghora Abraham Global LLC | bhutadamarasena.com
 *
 * Provides edge-compatible custom hashing and verification for Cloudflare Workers,
 * D1 database row verification, and session signature validation.
 */

export type CustomHashFunction = (data: string, saltOrSecret?: string) => Promise<string> | string;
export type CustomHashVerifier = (data: string, hash: string, saltOrSecret?: string) => Promise<boolean> | boolean;

export interface CustomSignedPayload<T = any> {
  data: T;
  timestamp: number;
  hash: string;
  algorithm: string;
}

export function aghoraBitwiseHash(input: string, salt: string = "aghora_abraham_2026"): string {
  const combined = `${salt}:${input}:${salt.split("").reverse().join("")}`;
  let h1 = 0xdeadbeef ^ combined.length;
  let h2 = 0x41c6ce57 ^ combined.length;
  let h3 = 0x7fffffff ^ (combined.length * 31);
  let h4 = 0x811c9dc5 ^ (combined.length * 17);

  for (let i = 0; i < combined.length; i++) {
    const ch = combined.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h1 = ((h1 << 13) | (h1 >>> 19)) ^ h2;

    h2 = Math.imul(h2 ^ ch, 1597334677);
    h2 = ((h2 << 17) | (h2 >>> 15)) ^ h3;

    h3 = Math.imul(h3 ^ (ch * 33), 2246822507);
    h3 = ((h3 << 11) | (h3 >>> 21)) ^ h4;

    h4 = Math.imul(h4 ^ (ch + i), 3266489909);
    h4 = ((h4 << 7) | (h4 >>> 25)) ^ h1;
  }

  h1 ^= h1 >>> 16;
  h1 = Math.imul(h1, 2246822507);
  h1 ^= h1 >>> 13;

  h2 ^= h2 >>> 16;
  h2 = Math.imul(h2, 3266489909);
  h2 ^= h2 >>> 13;

  h3 ^= h3 >>> 15;
  h3 = Math.imul(h3, 2654435761);
  h3 ^= h3 >>> 16;

  h4 ^= h4 >>> 14;
  h4 = Math.imul(h4, 1597334677);
  h4 ^= h4 >>> 15;

  const hex1 = (h1 >>> 0).toString(16).padStart(8, "0");
  const hex2 = (h2 >>> 0).toString(16).padStart(8, "0");
  const hex3 = (h3 >>> 0).toString(16).padStart(8, "0");
  const hex4 = (h4 >>> 0).toString(16).padStart(8, "0");

  return `agh_${hex1}${hex2}${hex3}${hex4}`;
}

/**
 * Aghora 64-Bit Circular Rolling Bitwise Avalanche Hash
 * Certified in Ancient Runes Monograph (Definition 5.1):
 * H_0 = 0x9E3779B97F4A7C15 (Golden Ratio Constant in Z_{2^64})
 * h_{k+1} = ((h_k <<< 13) ^ (b_k * 0x517CC1B727220A95)) + (h_k >>> 29) mod 2^64
 * h_final = h_L ^ ((h_L >> 33) * 0xFF51AFD7ED558CCD) mod 2^64
 */
export function aghoraBitwiseHash64(input: string | Uint8Array): string {
  const bytes = typeof input === "string" ? new TextEncoder().encode(input) : input;
  const MASK_64 = 0xFFFFFFFFFFFFFFFFn;
  const MULT_BYTE = 0x517CC1B727220A95n;
  const AVALANCHE_MULT = 0xFF51AFD7ED558CCDn;

  let h = 0x9E3779B97F4A7C15n;

  for (let i = 0; i < bytes.length; i++) {
    const b = BigInt(bytes[i]);
    const rotl13 = ((h << 13n) | (h >> (64n - 13n))) & MASK_64;
    const byteTerm = (b * MULT_BYTE) & MASK_64;
    const rotr29 = (h >> 29n) & MASK_64;
    h = ((rotl13 ^ byteTerm) + rotr29) & MASK_64;
  }

  const shifted = (h >> 33n) & MASK_64;
  const avTerm = (shifted * AVALANCHE_MULT) & MASK_64;
  const hFinal = (h ^ avTerm) & MASK_64;

  return hFinal.toString(16).padStart(16, "0");
}

export class EdgeCustomHashingEngine {
  private static instance: EdgeCustomHashingEngine;
  private algorithmName: string = "aghora_custom_v1";
  private customHashFn: CustomHashFunction | null = null;
  private customVerifierFn: CustomHashVerifier | null = null;
  private defaultSalt: string = "aghora_abraham_global_bhutadamarasena";

  public static getInstance(): EdgeCustomHashingEngine {
    if (!EdgeCustomHashingEngine.instance) {
      EdgeCustomHashingEngine.instance = new EdgeCustomHashingEngine();
    }
    return EdgeCustomHashingEngine.instance;
  }

  public registerCustomAlgorithm(
    name: string,
    hashFn: CustomHashFunction,
    verifierFn?: CustomHashVerifier
  ): void {
    this.algorithmName = name;
    this.customHashFn = hashFn;
    this.customVerifierFn = verifierFn || null;
  }

  public setDefaultSalt(salt: string): void {
    this.defaultSalt = salt;
  }

  public getAlgorithmName(): string {
    return this.algorithmName;
  }

  public async computeHash(data: string, saltOrSecret?: string): Promise<string> {
    const salt = saltOrSecret || this.defaultSalt;
    if (this.customHashFn) {
      return await this.customHashFn(data, salt);
    }
    return aghoraBitwiseHash(data, salt);
  }

  public async verifyHash(data: string, hash: string, saltOrSecret?: string): Promise<boolean> {
    if (!hash) return false;
    const salt = saltOrSecret || this.defaultSalt;

    if (this.customVerifierFn) {
      return await this.customVerifierFn(data, hash, salt);
    }

    const calculated = await this.computeHash(data, salt);
    return calculated === hash;
  }

  public async signPayload<T = any>(data: T, saltOrSecret?: string): Promise<CustomSignedPayload<T>> {
    const timestamp = Date.now();
    const rawString = JSON.stringify({ data, timestamp });
    const hash = await this.computeHash(rawString, saltOrSecret);

    return {
      data,
      timestamp,
      hash,
      algorithm: this.algorithmName,
    };
  }

  public async verifyPayload<T = any>(payload: CustomSignedPayload<T>, saltOrSecret?: string): Promise<boolean> {
    if (!payload || !payload.hash || !payload.timestamp) return false;
    const rawString = JSON.stringify({ data: payload.data, timestamp: payload.timestamp });
    return await this.verifyHash(rawString, payload.hash, saltOrSecret);
  }
}

export const edgeCustomHashing = EdgeCustomHashingEngine.getInstance();
